import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Users, Calendar, FileText } from 'lucide-react';
import { HubspotStatusBanner } from './HubspotComponents';

export const OverviewTab = ({ inquiries, clients, events, blogPosts, hubspotStatus, onRefreshHubspot }) => {
  const newInquiries = inquiries.filter(i => i.status === 'new').length;
  const activeEvents = events.filter(e => e.status !== 'completed').length;

  const stats = [
    { label: 'New Inquiries', value: newInquiries, icon: Mail, color: 'text-blue-600' },
    { label: 'Total Clients', value: clients.length, icon: Users, color: 'text-green-600' },
    { label: 'Active Events', value: activeEvents, icon: Calendar, color: 'text-[#D4AF37]' },
    { label: 'Blog Posts', value: blogPosts.length, icon: FileText, color: 'text-purple-600' },
  ];

  return (
    <div data-testid="admin-overview">
      <h2 className="font-heading text-2xl text-white mb-8">Dashboard Overview</h2>

      <HubspotStatusBanner status={hubspotStatus} onRefresh={onRefreshHubspot} />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-[#0a0a0a] border border-white/10 p-6"
          >
            <stat.icon className={`w-8 h-8 ${stat.color} mb-4`} />
            <p className="text-3xl font-heading text-white">{stat.value}</p>
            <p className="text-white/60 text-sm mt-1">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Recent Inquiries */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6 mb-8">
        <h3 className="font-heading text-xl text-white mb-4">Recent Inquiries</h3>
        {inquiries.length === 0 ? (
          <p className="text-white/40">No inquiries yet</p>
        ) : (
          <div className="space-y-3">
            {inquiries.slice(0, 5).map((inquiry) => (
              <div key={inquiry.inquiry_id} className="flex items-center justify-between p-3 bg-[#050505]">
                <div>
                  <p className="text-white font-medium">{inquiry.name}</p>
                  <p className="text-white/60 text-sm">{inquiry.event_type} • {inquiry.email}</p>
                </div>
                <span className={`text-xs uppercase px-2 py-1 ${
                  inquiry.status === 'new' ? 'bg-blue-50 text-blue-600' :
                  inquiry.status === 'contacted' ? 'bg-yellow-50 text-yellow-600' :
                  'bg-green-50 text-green-600'
                }`}>
                  {inquiry.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Events */}
      <div className="bg-[#0a0a0a] border border-white/10 p-6">
        <h3 className="font-heading text-xl text-white mb-4">Active Events</h3>
        {events.length === 0 ? (
          <p className="text-white/40">No events yet</p>
        ) : (
          <div className="space-y-3">
            {events.filter(e => e.status !== 'completed').slice(0, 5).map((event) => (
              <div key={event.event_id} className="flex items-center justify-between p-3 bg-[#050505]">
                <div>
                  <p className="text-white font-medium">{event.title}</p>
                  <p className="text-white/60 text-sm">{event.event_type} • {event.event_date}</p>
                </div>
                <span className={`text-xs uppercase px-2 py-1 ${
                  event.status === 'planning' ? 'bg-blue-50 text-blue-600' :
                  event.status === 'confirmed' ? 'bg-green-50 text-green-600' :
                  'bg-[#D4AF37]/20 text-[#D4AF37]'
                }`}>
                  {event.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
